import { describeApiError, type PointsSystem } from '@brush/api-client';
import {
  ErrorNotice,
  PlatformBadge,
  blankDefinition,
  shrpoDefinition,
  toDefinitionDocument,
} from '@brush/ui';
import { useState } from 'react';
import { useClonePointsSystem, useCreatePointsSystem } from '../../queries';
import { useRoom } from '../../room';
import { Modal, btnGhost, btnPrimary, modalInput } from '../Modal';

/**
 * What the dialog was opened to do.
 *
 * `clone` is the server's own clone route, not a create with a copied body — that is what §2.2
 * describes and it is what a room does with a platform system it wants to change. The copy lands
 * as version 1, `draft`, whatever it was copied from, and records where it came from. Cloning
 * checks two entitlements server-side: seeing the source, and writing to this room.
 */
export type NewSystemIntent =
  | { mode: 'create'; start: 'blank' | 'shrpo' }
  | { mode: 'clone'; source: PointsSystem; versionNumber: number | null };

export function NewPointsSystemDialog({
  intent,
  onClose,
  onCreated,
}: {
  intent: NewSystemIntent;
  onClose: () => void;
  /** Hands back the new system so the view can open the builder on it straight away. */
  onCreated: (system: PointsSystem) => void;
}) {
  const { roomId, room } = useRoom();
  const cloning = intent.mode === 'clone';

  const [name, setName] = useState(() =>
    cloning
      ? // A room taking a platform system keeps its name: it is that system, as this room runs
        // it. Copying its own system gets a "(copy)" so the two are tellable apart in the list.
        intent.source.isPlatform
        ? intent.source.name
        : `${intent.source.name} (copy)`
      : intent.start === 'shrpo'
        ? 'SHRPO-style tournament leaderboard'
        : '',
  );

  const create = useCreatePointsSystem(roomId);
  const clone = useClonePointsSystem(roomId);
  const pending = create.isPending || clone.isPending;

  const submit = () => {
    const trimmed = name.trim();
    if (trimmed.length === 0) return;

    if (cloning) {
      clone.mutate(
        { id: intent.source.id, name: trimmed, version: intent.versionNumber },
        { onSuccess: onCreated },
      );
      return;
    }

    const definition =
      intent.start === 'shrpo' ? shrpoDefinition(trimmed) : blankDefinition(trimmed);

    create.mutate(
      {
        name: trimmed,
        description: definition.description,
        definition: toDefinitionDocument(definition),
      },
      { onSuccess: onCreated },
    );
  };

  return (
    <Modal
      title={cloning ? 'Make this system your own' : 'New points system'}
      subtitle={
        cloning
          ? `A copy owned by ${room?.name ?? 'this room'}, free to change.`
          : `Owned by ${room?.name ?? 'this room'}. Nobody else can see or use it.`
      }
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose} className={btnGhost}>
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={name.trim().length === 0 || pending}
            className={btnPrimary}
          >
            {pending ? 'Creating…' : cloning ? 'Create copy' : 'Create and open'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <label className="block">
          <span className="mb-1 block text-xs text-ink-800/60">Name</span>
          <input
            type="text"
            value={name}
            autoFocus
            maxLength={200}
            onChange={(e) => setName(e.target.value)}
            placeholder="Summer Series Player of the Year"
            className={modalInput}
          />
        </label>

        {cloning ? (
          <div className="space-y-2 text-xs leading-relaxed text-ink-800/60">
            <p className="flex flex-wrap items-center gap-2">
              Copying
              {intent.source.isPlatform ? <PlatformBadge /> : null}
              <span className="font-medium text-ink-950">{intent.source.name}</span>
              {intent.versionNumber !== null ? (
                <span className="num text-ink-800/45">version {intent.versionNumber}</span>
              ) : null}
            </p>
            <p>
              The copy starts as your own draft and is independent from the moment it exists —
              changes to it never touch the original, and changes to the original never reach it.
              Nothing is published until you publish it.
            </p>
            {intent.versionNumber === null ? (
              <p className="text-warn">
                That system has no versions yet, so there is nothing to copy from — the copy will
                start empty.
              </p>
            ) : null}
          </div>
        ) : (
          <p className="text-xs leading-relaxed text-ink-800/60">
            {intent.start === 'shrpo'
              ? 'Starts from the published SHRPO ladder: 100 for a win down to a flat 10 from 28th, weighted by field size, a point for every entry that didn’t cash. Every figure is yours to change.'
              : 'Starts from a plain ladder covering the next three months, with no weighting and no points for busting out. You set the dates and the table on the next screen.'}
          </p>
        )}

        <ErrorNotice message={describeApiError(create.error) ?? describeApiError(clone.error)} />
      </div>
    </Modal>
  );
}
